import React, { useState, useEffect, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { useCheckInButton } from '@/hooks/admin/useCheckInButton';
import TShirtSizeModal from '../scanner/components/TShirtSizeModal';
import UndoCheckInButton from './check-in/UndoCheckInButton';
import PerformCheckInButton from './check-in/PerformCheckInButton';
import { useDirectDatabaseUpdate } from './check-in/useDirectDatabaseUpdate';

interface CheckInButtonProps {
  attendee: Attendee;
  onSuccess: (attendee: Attendee, timestamp: string) => void;
  onDirectUpdate?: (code: string, updates: any) => Promise<any>;
  onSilentUpdate?: (attendee: Attendee) => void;
}

const CheckInButton: React.FC<CheckInButtonProps> = ({ 
  attendee, 
  onSuccess,
  onDirectUpdate,
  onSilentUpdate
}) => {
  const originalTShirtSize = useRef(attendee.t_shirt_size);
  const [showTShirtModal, setShowTShirtModal] = useState(false);
  const modalTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (attendee.t_shirt_size) {
      originalTShirtSize.current = attendee.t_shirt_size;
    }
  }, [attendee.t_shirt_size]);
  
  // Hook for direct database updates
  const { isUpdating: isDbUpdating, directDatabaseUpdate } = useDirectDatabaseUpdate(
    attendee,
    onSuccess,
    onSilentUpdate
  );
  
  // Get state from the useCheckInButton hook
  const { isUpdating: isButtonUpdating } = useCheckInButton(
    attendee, 
    onSuccess, 
    onDirectUpdate, 
    onSilentUpdate
  );
  
  // Combined updating state
  const isUpdating = isDbUpdating || isButtonUpdating;
  
  // Handler for direct updates
  const handleDirectUpdate = async (code: string, updates: any) => {
    try {
      // Ensure we're not already processing an update
      if (isUpdating) {
        console.log('Skipping update as one is already in progress');
        return null;
      }
      
      // If we have an external direct update handler, use it
      if (onDirectUpdate) {
        const result = await onDirectUpdate(code, updates);
        
        // Show the T-shirt modal if this was a successful check-in
        if (result && updates.attended === true) {
          safelyShowTShirtModal();
        }
        
        return result;
      } 
      
      // Otherwise use our internal direct database update
      const result = await directDatabaseUpdate(code, updates);
      
      // Show the T-shirt modal if this was a successful check-in
      if (result && updates.attended === true) {
        safelyShowTShirtModal();
      }
      
      return result;
    } catch (error) {
      console.error('Error in direct update:', error);
      return null;
    }
  };
  
  // Function to safely show T-shirt modal with debounce
  const safelyShowTShirtModal = () => {
    // Clear any existing timer
    if (modalTimerRef.current) {
      clearTimeout(modalTimerRef.current);
      modalTimerRef.current = null;
    }
    
    // Set a small delay to ensure UI is ready
    modalTimerRef.current = setTimeout(() => {
      console.log('Safely showing T-shirt modal with debounce');
      setShowTShirtModal(true);
    }, 100);
  };
  
  // Function to safely close T-shirt modal
  const safelyCloseTShirtModal = () => {
    console.log('safelyCloseTShirtModal called');
    // Clear any existing timer
    if (modalTimerRef.current) {
      clearTimeout(modalTimerRef.current);
      modalTimerRef.current = null;
    }
    
    setShowTShirtModal(false);
  };
  
  // Listen for attendee-checked-in events to display T-shirt modal
  useEffect(() => {
    const handleAttendeeCheckedIn = (event: CustomEvent) => {
      // Always show T-shirt modal for this attendee on check-in, regardless of flags
      if (event.detail?.attendee?.code === attendee.code) {
        console.log('Event triggered modal show for', attendee.name);
        safelyShowTShirtModal();
      }
    };
    
    window.addEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
    
    return () => {
      // Clean up event listener and any timers
      window.removeEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
      
      if (modalTimerRef.current) {
        clearTimeout(modalTimerRef.current);
        modalTimerRef.current = null;
      }
    };
  }, [attendee.code, attendee.name]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (modalTimerRef.current) {
        clearTimeout(modalTimerRef.current);
        modalTimerRef.current = null;
      }
    };
  }, []);

  // Render different buttons based on attendee's attendance status
  if (attendee.attended) {
    return (
      <>
        <UndoCheckInButton 
          attendee={attendee}
          isUpdating={isUpdating}
          onDirectUpdate={handleDirectUpdate}
        />
        
        {showTShirtModal && (
          <TShirtSizeModal
            open={showTShirtModal}
            onClose={safelyCloseTShirtModal}
            attendeeName={attendee.name}
            tShirtSize={attendee.t_shirt_size || originalTShirtSize.current}
            autoCloseDelay={3000} // Reduced from 5000 to prevent blocking UI too long
          />
        )}
      </>
    );
  }

  return (
    <>
      <PerformCheckInButton
        attendee={attendee}
        isUpdating={isUpdating}
        onDirectUpdate={handleDirectUpdate}
        setShowTShirtModal={safelyShowTShirtModal}
      />
      
      {showTShirtModal && (
        <TShirtSizeModal
          open={showTShirtModal}
          onClose={safelyCloseTShirtModal}
          attendeeName={attendee.name}
          tShirtSize={attendee.t_shirt_size || originalTShirtSize.current}
          autoCloseDelay={3000} // Reduced from 5000 to prevent blocking UI too long
        />
      )}
    </>
  );
};

export default CheckInButton;
